import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Which /dashboard/typography page group a public page belongs to. The group is
// put on <html data-jet-group="…">, and the published CSS re-sets the variables
// a group overrides under that attribute (server/utils/typography/
// renderTypographyCss.js). Keys must match TYPE_PAGE_GROUPS in
// server/utils/typography/typeRoles.js, which also holds the labels and the
// page lists the dashboard shows.

const EXACT = {
  '/': 'home',
  '/home': 'home',
  '/coventry-homes': 'home',
  '/crescent-homes': 'home',

  '/loan-programs': 'loans',
  '/purchase': 'loans',
  '/refinance': 'loans',
  // Program pages live at top-level slugs (components/slugrouter/slugrouter.js).
  '/conventional': 'loans',
  '/va': 'loans',
  '/fha': 'loans',
  '/usda': 'loans',
  '/refinancing': 'loans',
  '/jumbo': 'loans',
  '/first-time-homebuyers': 'loans',
  '/low-down-payment': 'loans',
  '/dscr': 'loans',
  '/arm': 'loans',

  '/blog': 'blog',

  '/find-a-loan-officer': 'locations',
  '/branchlocations': 'locations',

  '/calculators': 'tools',
  '/contact': 'tools',
  '/getstarted': 'tools',
  '/submit-payment': 'tools',
  '/opt-out': 'tools',

  '/privacy': 'legal',
  '/licensing': 'legal',
  '/security': 'legal',
  '/terms': 'legal',
  '/ccpa': 'legal',
  '/data-partners': 'legal',
  '/business-profiles': 'legal',
  '/team-jet': 'legal',

  '/landingpage': 'partner',
  '/exclusiveleads': 'partner',
  '/consumerengagement': 'partner',
  '/clientacquisition': 'partner',
  '/portfolioretention': 'partner',
};

const PREFIXES = [
  ['/loan-programs/', 'loans'],
  ['/blog/', 'blog'],
  ['/branch/', 'locations'],
  ['/state/', 'locations'],
  ['/group/', 'locations'],
];

// Group for a path, or null. Other single-segment slugs (loan officer profiles,
// builder intake forms) are only known once SlugRouter resolves them; it sets
// the group itself with useTypographyGroup().
export function typographyGroupForPath(pathname) {
  let p = (pathname || '/').split('?')[0].split('#')[0].toLowerCase();
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  if (EXACT[p]) return EXACT[p];
  const hit = PREFIXES.find(([prefix]) => p.startsWith(prefix));
  return hit ? hit[1] : null;
}

// A page the dashboard preview opens to show each group.
export const GROUP_SAMPLE_PATH = {
  home: '/home',
  loans: '/va',
  blog: '/blog',
  locations: '/find-a-loan-officer',
  tools: '/contact',
  legal: '/privacy',
  partner: '/landingpage',
};

export function setTypographyGroup(group) {
  const html = document.documentElement;
  if (group) html.setAttribute('data-jet-group', group);
  else html.removeAttribute('data-jet-group');
}

// For pages whose group is only known at runtime (SlugRouter). Runs after
// TypographyScope's path-based assignment (which resets it on every navigation),
// so it re-applies on each path change too — e.g. one LO profile to another.
export function useTypographyGroup(group) {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    if (group) setTypographyGroup(group);
  }, [group, pathname]);
}
